/**
 * custom-module-identity.ts — WSPÓLNE ŹRÓDŁO walidacji nazwy + slugu kartoteki własnej.
 *
 * Jeden slugify + jeden wzorzec slugu + jedna lista slugów zarezerwowanych, używane
 * IDENTYCZNIE przez:
 *   - frontend (formularz „Utwórz kartotekę" u tenanta i u admina — walidacja na żywo),
 *   - backend  (POST /api/modules, endpointy duplikacji — walidacja twarda).
 *
 * Wcześniej logika była powielona w kilku miejscach (każde z własnym `slugify` i regexem):
 * apps/admin/.../CreateKartotekaInline.tsx, CreateKartotekaViewModal.tsx,
 * AddKartotekaFieldModal.tsx oraz inline `nameToSlug`/`ensureUniqueSlug` w
 * apps/api/app/api/modules/route.ts. Ten plik zastępuje te kopie.
 *
 * NIE importuj tu nic z pg / fs / node — paczka jest bundlowana też przez Vite/React SPA.
 */

/**
 * Wzorzec slugu kartoteki własnej — musi zaczynać się od litery, dalej litera/cyfra/
 * podkreślnik, maks. 49 znaków. Zgodny z walidatorem DDL po stronie API
 * (`MODULE_SLUG_REGEX` w apps/api/lib/ddl-kernel), bo slug staje się nazwą fizycznej
 * tabeli `<code>_module_<slug>` (limit Postgres 63: kod 6 + `_module_` 8 + slug ≤ 49).
 */
export const CUSTOM_MODULE_SLUG_PATTERN = /^[a-z][a-z0-9_]{0,48}$/;

/** Maks. długość slugu kartoteki własnej (limit fizycznej tabeli Postgres). */
export const CUSTOM_MODULE_SLUG_MAX_LENGTH = 49;

/** Maks. długość nazwy kartoteki (kolumna `custom_modules.name` varchar(100)). */
export const CUSTOM_MODULE_NAME_MAX_LENGTH = 100;

/**
 * Slugi zarezerwowane — kolidują z kartotekami SYSTEMOWYMI i klasami dokumentów.
 * Kartoteka własna z takim slugiem myliłaby użytkownika (dwie „faktury sprzedaży")
 * i mogłaby zderzyć się z routingiem/uprawnieniami. Backend dodatkowo sprawdza realną
 * tabelę `system_modules` (autorytatywnie) — ta lista jest szybkim pre-checkiem po stronie
 * klienta oraz siatką bezpieczeństwa gdy tabela niedostępna.
 *
 * Źródło: apps/api/lib/seed-system-modules.ts (SEED_MODULES + helper entities + klasy dok.).
 */
export const RESERVED_MODULE_SLUGS: ReadonlySet<string> = new Set([
  // encje systemowe top-level
  'contractors',
  'contractor_contacts',
  'companies',
  'users',
  'employees',
  'products',
  // encje pomocnicze
  'addresses',
  'contractor_bank_accounts',
  'company_bank_accounts',
  // Rename F24 Pakiet 0: encja pomocnicza to dziś `org_units`. Stary slug ZOSTAJE na liście —
  // rezerwacja jest jednostronna (nikt nie traci nic na tym, że `departments` pozostaje zajęte),
  // a tenanci utworzeni przed rename'em mogą mieć wpis o tej nazwie w `system_modules`.
  // Istniejące tenanty develop/stage są po wdrożeniu tej zmiany zakładane OD NOWA (decyzja
  // właściciela 2026-09-03, produkcji nie ma) — skryptu synchronizującego metadane modułów
  // systemowych na żywych tenantach nie ma i nie powstaje.
  'departments',
  'org_units',
  // klasy dokumentów
  'purchase_invoices',
  'sale_invoices',
  'contracts',
  'letters',
  'commercial',
  'internal',
  'erp_documents',
]);

/**
 * Generuje kanoniczny slug z nazwy kartoteki.
 *
 * Kroki: lowercase → strip diakrytyków (ł→l) → spacje/myślniki na `_` → usuń znaki spoza
 * [a-z0-9_] → zwiń wielokrotne `_` → obetnij `_` z brzegów → usuń wiodące nie-litery
 * (slug MUSI zaczynać się od litery) → przytnij do 49 znaków (+ ponowne obcięcie brzegowego `_`).
 *
 * Zwraca '' gdy z nazwy nie da się wyprowadzić poprawnego slugu (np. sama interpunkcja) —
 * callsite decyduje o fallbacku (np. 'kartoteka').
 */
export function slugifyModuleName(name: string): string {
  const base = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip diakrytyki
    .replace(/ł/g, 'l')
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[^a-z]+/, ''); // slug musi zaczynać się od litery
  return base.slice(0, CUSTOM_MODULE_SLUG_MAX_LENGTH).replace(/_+$/g, '');
}

/** Czy slug ma poprawny FORMAT (nie sprawdza unikalności ani rezerwacji). */
export function isValidModuleSlug(slug: string): boolean {
  return CUSTOM_MODULE_SLUG_PATTERN.test(slug);
}

/** Czy slug jest zarezerwowany dla kartoteki systemowej / klasy dokumentu. */
export function isReservedModuleSlug(slug: string): boolean {
  return RESERVED_MODULE_SLUGS.has(slug.toLowerCase());
}

/** Powód odrzucenia slugu (dla i18n po stronie UI). `null` = slug OK formalnie. */
export type ModuleSlugProblem = 'empty' | 'invalid' | 'reserved';

/**
 * Waliduje FORMAT slugu (bez zapytań do bazy). Zwraca kod problemu albo null.
 * Unikalność (kolizja z istniejącą kartoteką) sprawdza osobno callsite mający listę/bazę.
 */
export function checkModuleSlugFormat(slug: string): ModuleSlugProblem | null {
  if (!slug) return 'empty';
  if (!isValidModuleSlug(slug)) return 'invalid';
  if (isReservedModuleSlug(slug)) return 'reserved';
  return null;
}
